import { createPortal } from "react-dom";
import { Icon } from "../../ui";
import {
  SLOT_CLASS,
  actionAuditShortLabel,
  canonicalSlotValue,
  displaySlotValue,
  logEventDescription,
  slotClassLabel,
  slotClassTone,
  slotIsFoxlineDriftAlert,
  slotNeedsSoftoneResend,
  slotNotTouchedByOps,
  slotSoftoneChangeLocked,
  softoneAgreesWithInternal,
  softoneChangeLockExplanation,
  softoneChangeLockLabel,
} from "../../lib/softoneSlotSync";
import { formatScheduleChangeTime } from "./scheduleComponents";

/** Corner badge when SoftOne last edit was not WEB/Ops (263). */
export function SoftoneNotOpsMarker({ slot, lang, onClick }) {
  // Lock badge already means SoftOne owns the day — do not stack S1·UI on locked cells.
  if (slotSoftoneChangeLocked(slot)) return null;
  if (!slotNotTouchedByOps(slot)) return null;
  const gr = lang === "gr";
  const title = actionAuditShortLabel(slot, lang)
    || (gr ? "Αλλαγή στο SoftOne (όχι Ops)" : "Changed in SoftOne (not Ops)");
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      style={{
        position: "absolute",
        top: 2,
        right: 2,
        zIndex: 4,
        minWidth: 28,
        height: 14,
        padding: "0 4px",
        borderRadius: 4,
        fontSize: 8,
        fontWeight: 800,
        letterSpacing: 0.15,
        lineHeight: "12px",
        background: "#FCE7F3",
        color: "#9D174D",
        border: "1.5px solid #F9A8D4",
        boxShadow: "0 0 0 1px rgba(255,255,255,0.8)",
        cursor: "pointer",
        fontFamily: "DM Sans, sans-serif",
      }}
    >
      {gr ? "S1·UI" : "S1·UI"}
    </button>
  );
}

export function FoxlineSlotMarker({ classification, internalValue, foxlineValue, changeLocked = false, known = true, onClick, lang }) {
  const agrees = known && softoneAgreesWithInternal(internalValue, foxlineValue);
  if (!canonicalSlotValue(internalValue) && !canonicalSlotValue(foxlineValue)) return null;
  const gr = lang === "gr";
  const tone = agrees
    ? slotClassTone(SLOT_CLASS.IN_SYNC)
    : slotClassTone(classification && slotIsFoxlineDriftAlert(classification) ? classification : SLOT_CLASS.MANUAL_CHANGE);
  const title = !known
    ? (gr ? "Το SoftOne δεν έχει φορτωθεί" : "SoftOne is not loaded")
    : changeLocked
    ? softoneChangeLockLabel(lang)
    : agrees
    ? (canonicalSlotValue(internalValue)
      ? (gr ? "Ταιριάζει με το SoftOne" : "Matches SoftOne")
      : (gr ? "Κενό και στο SoftOne" : "SoftOne is empty too"))
    : (classification ? slotClassLabel(classification, lang) : (gr ? "Διαφέρει από το SoftOne" : "Differs from SoftOne"));
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      style={{
        position: "absolute",
        top: 2,
        left: 2,
        zIndex: 4,
        minWidth: changeLocked ? 34 : 28,
        height: 14,
        padding: "0 4px",
        borderRadius: 4,
        fontSize: 8,
        fontWeight: 800,
        letterSpacing: 0.15,
        lineHeight: "12px",
        background: changeLocked ? "#FEF3C7" : tone.bg,
        color: changeLocked ? "#92400E" : tone.color,
        border: `1.5px solid ${changeLocked ? "#F59E0B" : tone.border}`,
        boxShadow: "0 0 0 1px rgba(255,255,255,0.8)",
        cursor: "pointer",
        fontFamily: "DM Sans, sans-serif",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      }}
    >
      {changeLocked ? (
        <>
          <Icon name="lock" size={9} />
          <span>S1</span>
        </>
      ) : agrees ? (
        "S1✓"
      ) : (
        "S1✗"
      )}
    </button>
  );
}

/** SoftOne-owned day: lock label + SoftOne hours only (no Internal dual view). */
export function SoftoneLockedCellBody({ value, lang }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        lineHeight: 1.15,
        paddingTop: 12,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          fontSize: 9,
          fontWeight: 800,
          color: "#92400E",
          textTransform: "uppercase",
          letterSpacing: 0.3,
        }}
      >
        <Icon name="lock" size={10} />
        {softoneChangeLockLabel(lang)}
      </span>
      <span style={{ fontWeight: 800, fontSize: 11, color: "#92400E" }}>
        {displaySlotValue(value, lang)}
      </span>
    </div>
  );
}

export function FoxlineDriftCellBody({ slot, lang }) {
  // SoftOne-owned days never use dual Internal/SoftOne drift — caller must gate on !changeLocked.
  if (!slot || slotSoftoneChangeLocked(slot) || !slotIsFoxlineDriftAlert(slot.classification)) return null;
  const tone = slotClassTone(slot.classification);
  const t = (en, gr) => (lang === "gr" ? gr : en);
  const local = displaySlotValue(slot.local, lang);
  const fox = displaySlotValue(slot.foxline, lang);
  const headline =
    slot.classification === SLOT_CLASS.MANUAL_CHANGE
      ? t("Internal differs from SoftOne", "Εσωτερικό ≠ SoftOne")
      : slotClassLabel(slot.classification, lang);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        padding: "14px 4px 4px",
        lineHeight: 1.15,
      }}
    >
      <span style={{ fontSize: 9, fontWeight: 800, color: tone.color, textTransform: "uppercase", letterSpacing: 0.3 }}>
        {headline}
      </span>
      <span style={{ fontWeight: 800, fontSize: 11, color: tone.color }}>{local}</span>
      <span style={{ fontSize: 10, fontWeight: 800, color: tone.color }}>
        S1 {fox}
      </span>
    </div>
  );
}

function ValueRow({ label, value, lang, emphasize }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)" }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: emphasize ? 800 : 700, color: emphasize ? "var(--text-primary)" : "var(--text-secondary)" }}>
        {displaySlotValue(value, lang)}
      </div>
    </div>
  );
}

export function FoxlineSlotPopup({
  open,
  onClose,
  slot,
  history = [],
  lang,
  isHr,
  readOnly = false,
  busy,
  onUploadBlank,
  onMarkSubmitted,
  onAcceptDeletion,
  onRestore,
  onReplace,
  erganiRow = null,
  erganiHistory = [],
}) {
  if (!open || !slot) return null;
  const changeLocked = slotSoftoneChangeLocked(slot);
  const tone = changeLocked
    ? { bg: "#FEF3C7", color: "#92400E", border: "#F59E0B" }
    : slotClassTone(slot.classification);
  const t = (en, gr) => (lang === "gr" ? gr : en);
  const actions = [];
  if (isHr && !readOnly && !changeLocked && slot.classification === SLOT_CLASS.API_ELIGIBLE) {
    actions.push({ key: "upload", label: t("Upload to SoftOne", "Αποστολή στο SoftOne"), onClick: onUploadBlank });
  }
  if (isHr && !changeLocked && slot.classification === SLOT_CLASS.DELETED_IN_SOFTONE) {
    if (!readOnly) {
      actions.push({ key: "restore", label: t("Restore via API", "Επαναφορά μέσω API"), onClick: onRestore });
    }
    actions.push({ key: "accept", label: t("Accept deletion", "Αποδοχή διαγραφής"), onClick: onAcceptDeletion, outline: true });
  }
  // SoftOne owns the day — no Ops write actions (resend / mark-submitted).
  const canResend = isHr && !readOnly && !changeLocked && slotNeedsSoftoneResend(slot);
  if (canResend) {
    actions.push({
      key: "replace",
      label: t("Resend to SoftOne (cancel + upload)", "Επαναποστολή στο SoftOne (ακύρωση + αποστολή)"),
      onClick: onReplace,
    });
  }
  if (
    isHr &&
    !changeLocked &&
    (slot.classification === SLOT_CLASS.MANUAL_CHANGE ||
      slot.classification === SLOT_CLASS.LOCAL_MATCHES_FOXLINE ||
      slot.classification === SLOT_CLASS.FOXLINE_CHANGED ||
      slot.classification === SLOT_CLASS.CONFLICT)
  ) {
    actions.push({
      key: "mark",
      label: t("Mark submitted (updated in SoftOne)", "Σήμανση καταχωρημένου (ενημερώθηκε στο SoftOne)"),
      onClick: onMarkSubmitted,
      outline: true,
    });
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 13000,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface)",
          borderRadius: 14,
          border: "1px solid var(--border)",
          maxWidth: 440,
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          padding: 18,
          boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: "var(--text-primary)" }}>
              {slot.employee_name || slot.employee_code}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{slot.date}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("Close", "Κλείσιμο")}
            style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--text-secondary)" }}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <div
          style={{
            marginTop: 12,
            padding: "8px 10px",
            borderRadius: 10,
            background: tone.bg,
            border: `1px solid ${tone.border}`,
            color: tone.color,
            fontSize: 12,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {changeLocked ? <Icon name="lock" size={14} /> : null}
          {changeLocked ? softoneChangeLockLabel(lang) : slotClassLabel(slot.classification, lang)}
        </div>
        {changeLocked ? (
          <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 10, background: "#FFFBEB", border: "1px solid #F59E0B", color: "#92400E", fontSize: 12, fontWeight: 600, lineHeight: 1.45 }}>
            {softoneChangeLockExplanation(lang)}
          </div>
        ) : null}
        {!changeLocked && slotNotTouchedByOps(slot) ? (
          <div
            style={{
              marginTop: 10,
              padding: "8px 10px",
              borderRadius: 10,
              background: "#FDF2F8",
              border: "1px solid #F9A8D4",
              color: "#9D174D",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {t(
              "Last SoftOne change was not from Ops (WEB). ",
              "Η τελευταία αλλαγή στο SoftOne δεν έγινε από το Ops (WEB). "
            )}
            {actionAuditShortLabel(slot, lang)}
          </div>
        ) : null}
        {readOnly ? (
          <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 10, background: "#FFFBEB", border: "1px solid #F59E0B", color: "#92400E", fontSize: 12, fontWeight: 700 }}>
            {t("Prod simulation — SoftOne reads only. Uploads are disabled.", "Προσομοίωση παραγωγής — μόνο ανάγνωση SoftOne. Οι αποστολές είναι απενεργοποιημένες.")}
          </div>
        ) : null}

        <div style={{ marginTop: 12 }}>
          <ValueRow label={t("Internal", "Εσωτερικό")} value={slot.local} lang={lang} emphasize />
          <ValueRow label={t("Last sent", "Τελευταία αποστολή")} value={slot.submitted} lang={lang} />
          <ValueRow label="SoftOne" value={slot.foxline} lang={lang} />
          <ValueRow label={t("Ergani", "Εργάνη")} value={erganiRow?.shift_value || ""} lang={lang} />
        </div>

        {actions.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
            {actions.map((a) => (
              <button
                key={a.key}
                type="button"
                className={a.outline ? "btn-base btn-outline" : "btn-base btn-green"}
                disabled={busy}
                onClick={a.onClick}
                style={{ minHeight: 36, justifyContent: "center" }}
              >
                {a.label}
              </button>
            ))}
          </div>
        ) : null}

        <div style={{ marginTop: 16, fontSize: 12, fontWeight: 800, color: "var(--text-primary)" }}>
          {t("Ergani history", "Ιστορικό Εργάνη")}
        </div>
        {erganiHistory.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>
            {t("No Ergani versions for this day.", "Δεν υπάρχουν εκδόσεις Εργάνη για αυτή την ημέρα.")}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            {erganiHistory.map((row, i) => (
              <div key={row.source_import_id || i} style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                {row.filename || "XML"} · {row.shift_value} {row.uploaded_at ? `· ${row.uploaded_at}` : ""}
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 16, fontSize: 12, fontWeight: 800, color: "var(--text-primary)" }}>
          {t("History", "Ιστορικό")}
        </div>
        {history.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>
            {t("No logged events for this slot yet.", "Δεν υπάρχουν καταγεγραμμένα γεγονότα για αυτό το slot.")}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
            {history.map((row, i) => (
              <div key={row.id || i} style={{ background: "var(--surface2)", borderRadius: 8, padding: "8px 10px", border: "1px solid var(--border)" }}>
                <div style={{ fontSize: 12, color: "var(--text-primary)", fontWeight: 700 }}>
                  {logEventDescription(row, lang)}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>
                  {formatScheduleChangeTime(row.changed_at)} {row.changed_by_name ? (lang === "gr" ? `από ${row.changed_by_name}` : `by ${row.changed_by_name}`) : ""}
                  {row.source ? ` · ${row.source}` : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
